/**
 * TransferToAIEditor
 *
 * Workflow node editor for the generic "AI Chatbot" action.
 *
 * Architecture:
 *   ① AI Config     — which LLM + the system prompt
 *   ② MCP Servers   — optional tool servers (if you want the AI to look things up)
 *   ③ Specialists   — optional: other AI Configs this AI can delegate sub-topics to
 *
 * This editor is for a general AI chatbot with no specific external data source.
 * For data-source-specific nodes, see NetsuiteAIEditor, ShopifyAIEditor, etc.
 */
import * as React from "react";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import useAIConfigs from "@/hooks/useAIConfigs";
import useMcpServers from "@/hooks/useMcpServers";
import AISetupGuide from "./AISetupGuide";
import AISpecialistSection from "./AISpecialistSection";
import AIJumpTargetSection from "./AIJumpTargetSection";
import AIEscalationSection from "./AIEscalationSection";
import AIContextInspector from "./AIContextInspector";
import McpStaleBadge from "./McpStaleBadge";
import ChannelOverrideEditor from "@/pages/Messaging/Bots/ChannelOverrideEditor";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

// AI providers that support MCP tool calling
const MCP_SUPPORTED_PROVIDERS = ["claude", "openai", "azure-openai", "anthropic"];

export default function TransferToAIEditor({ node, onUpdate }: Props) {
  const { aiConfigurations } = useAIConfigs();
  const { items: mcpServers } = useMcpServers();
  const [showChannelOverrides, setShowChannelOverrides] = React.useState(false);

  const aiOptions = aiConfigurations.map((ai) => ({ value: ai.id, label: ai.name }));

  const selectedAiConfig = aiConfigurations.find((ai) => ai.id === node.aiConfigId);
  const enabledMcpServers = mcpServers.filter((s) => s.enabled);

  const providerName =
    (selectedAiConfig as { provider?: string } | undefined)?.provider?.toLowerCase() ?? "";
  const providerUnsupported =
    enabledMcpServers.length > 0 &&
    selectedAiConfig != null &&
    providerName !== "" &&
    !MCP_SUPPORTED_PROVIDERS.some((p) => providerName.includes(p));

  const selectedMcpIds: number[] = Array.isArray(node.mcpServerIds) ? node.mcpServerIds : [];

  const enabledToolNames = enabledMcpServers
    .filter((s) => selectedMcpIds.includes(s.id))
    .flatMap((s) => s.toolNames ?? []);

  function toggleMcpServer(id: number, checked: boolean) {
    const next = checked ? [...selectedMcpIds, id] : selectedMcpIds.filter((x) => x !== id);
    onUpdate({ mcpServerIds: next.length > 0 ? next : undefined });
  }

  return (
    <>
      {/* Setup guide — collapsible how-it-works card */}
      <AISetupGuide
        hasAiConfig={!!selectedAiConfig}
        hasMcpServers={enabledMcpServers.length > 0}
      />

      {/* ── Step 1: AI Config ── */}
      <FormGroup label="AI Config">
        {aiConfigurations.length === 0 ? (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            No AI configs found.{" "}
            <a href="/bots" className="underline font-medium">
              Create an AI Config first →
            </a>
            <p className="mt-1 text-slate-500">
              The AI Config holds the system prompt — it tells the AI how to behave and what it
              can do.
            </p>
          </div>
        ) : (
          <Select
            options={[{ value: 0, label: "— Select AI Config —" }, ...aiOptions]}
            value={node.aiConfigId ?? 0}
            onChange={(value) => onUpdate({ aiConfigId: Number(value) || undefined })}
          />
        )}
        {selectedAiConfig && !selectedAiConfig.prompt && (
          <p className="text-xs text-amber-600 mt-1">
            This AI config has no system prompt.{" "}
            <a href="/bots" className="underline">
              Add a prompt →
            </a>{" "}
            to define how this AI behaves.
          </p>
        )}
        {selectedAiConfig && selectedAiConfig.prompt && (
          <div className="flex items-center gap-2 mt-1.5">
            <a
              href={`/bots?config=${selectedAiConfig.id}`}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline"
            >
              Edit Prompt →
            </a>
            <button
              type="button"
              onClick={() => setShowChannelOverrides((v) => !v)}
              className="text-xs text-slate-500 hover:text-blue-600 font-medium hover:underline"
              aria-expanded={showChannelOverrides}
            >
              {showChannelOverrides ? "Hide Channel Overrides" : "Channel Overrides"}
            </button>
          </div>
        )}
      </FormGroup>

      {/* ── Channel Prompt Overrides ── */}
      {showChannelOverrides && selectedAiConfig && (
        <div className="mt-2 pt-3 border-t border-slate-200">
          <ChannelOverrideEditor
            aiConfigId={selectedAiConfig.id}
            botPrompt={selectedAiConfig.prompt}
            onClose={() => setShowChannelOverrides(false)}
          />
        </div>
      )}

      {/* ── MCP Tool Servers (always visible) ── */}
      <FormGroup label="MCP Tool Servers">
        {enabledMcpServers.length === 0 ? (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2.5">
            No MCP tool servers configured yet.{" "}
            <a href="/integrations/mcp" className="text-blue-600 hover:underline font-medium">
              Add an MCP Tool Server →
            </a>
            <p className="mt-1.5 text-slate-400">
              MCP servers give this AI tools to call — look up orders, search records, query APIs,
              etc. Configure one in <span className="font-medium">Integrations → MCP Servers</span>,
              then come back to enable it here.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 mt-1">
            {providerUnsupported && (
              <p className="text-amber-700 bg-amber-50 text-xs px-2 py-1 rounded border border-amber-200">
                MCP tools require Claude or OpenAI. The selected AI config may not support them.
              </p>
            )}
            {enabledMcpServers.map((server) => {
              const checked = selectedMcpIds.includes(server.id);
              return (
                <label
                  key={server.id}
                  className={`flex gap-2.5 cursor-pointer rounded border p-2.5 transition-colors ${
                    checked
                      ? "border-blue-300 bg-blue-50"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 shrink-0"
                    checked={checked}
                    onChange={(e) => toggleMcpServer(server.id, e.target.checked)}
                    aria-label={`Enable MCP server: ${server.name}`}
                  />
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{server.name}</span>
                      {server.toolCount > 0 && (
                        <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">
                          {server.toolCount} {server.toolCount === 1 ? "tool" : "tools"}
                        </span>
                      )}
                      <McpStaleBadge lastDiscoveredAt={server.lastDiscoveredAt} />
                    </div>
                    {server.description && (
                      <span className="text-xs text-slate-500">{server.description}</span>
                    )}
                    {server.toolNames.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {server.toolNames.slice(0, 6).map((t) => (
                          <span
                            key={t}
                            className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono"
                          >
                            {t}
                          </span>
                        ))}
                        {server.toolNames.length > 6 && (
                          <span className="text-xs text-slate-400 px-1 py-0.5">
                            +{server.toolNames.length - 6} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </FormGroup>

      {/* ── Context Inspector (debug) ── */}
      <AIContextInspector mcpToolNames={enabledToolNames} />

      {/* ── Specialist Agents (orchestration) ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AISpecialistSection
          node={node}
          onUpdate={onUpdate}
          aiConfigurations={aiConfigurations}
          enabledMcpServers={enabledMcpServers}
        />
      </div>

      {/* ── Escalation & Handoff ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIEscalationSection node={node} onUpdate={onUpdate} />
      </div>

      {/* ── Jump to Workflow Section (advanced / custom) ── */}
      <div className="mt-2 pt-3 border-t border-slate-200">
        <AIJumpTargetSection node={node} onUpdate={onUpdate} />
      </div>
    </>
  );
}
